/**
 * PageScope - 页面 Extend 生命周期：commands + subscribe 自动登记，路由离开时统一清理。
 */
import { createWatchCommands, useAndStore } from "@nuwatop/code-canvas-core";

export type PageScopeSetupOptions = {
  commands?: () => Record<string, (...args: unknown[]) => void>;
  /** 非 subscribe 的清理（重置模块 flag、storeRef 等） */
  onLeave?: () => void;
};

export type PageScope = {
  readonly sessionId: string;
  setup(store: any, options?: PageScopeSetupOptions): void;
  subscribe<T>(
    selector: (state: any) => T,
    listener: (value: T, prevValue: T) => void,
  ): void;
};

const cleanupsByPrefix = new Map<string, () => void>();

/** 路由离开时在清 store 之前调用 */
export function runPageScopeCleanups(sessionId: string) {
  const dispose = cleanupsByPrefix.get(sessionId);
  if (!dispose) return;
  try {
    dispose();
  } catch (error) {
    console.error("[PageScope] runPageScopeCleanups failed:", sessionId, error);
  }
  cleanupsByPrefix.delete(sessionId);
}

export function createPageScope(sessionId: string): PageScope {
  let bag: Array<() => void> = [];

  const dispose = () => {
    while (bag.length) {
      const fn = bag.pop();
      try {
        fn?.();
      } catch (error) {
        console.error("[PageScope] cleanup failed:", sessionId, error);
      }
    }
  };

  return {
    sessionId,

    setup(store: any, options?: PageScopeSetupOptions) {
      if (!sessionId || !store) return;

      runPageScopeCleanups(sessionId);
      bag = [];

      if (options?.commands) {
        const unsub = createWatchCommands(options.commands)(store, sessionId);
        if (typeof unsub === "function") {
          bag.push(unsub);
        }
      }

      if (options?.onLeave) {
        bag.push(options.onLeave);
      }

      cleanupsByPrefix.set(sessionId, dispose);
    },

    subscribe<T>(
      selector: (state: any) => T,
      listener: (value: T, prevValue: T) => void,
    ) {
      const unsub = (useAndStore as any).subscribe(selector, listener);
      if (typeof unsub === "function") {
        bag.push(unsub);
      }
    },
  };
}
